import { Icon, IconProps, Tooltip, BadgeNumber, BadgeText, Stack, Text } from '@cellula/react';
import { withTranslation } from '@application/i18n/with_translation';
import { tokens } from '@cellula/react-theme-patient';
import { Fragment } from 'react';
import styled from 'styled-components';
import { CounterState, useCounterState } from '../../application/counter';

interface Props {
	isNew?: boolean;
	label?: string;
	reservedUntil?: CounterState;
	labelIcon?: IconProps['type'];
	pendingPayment: number;
	formsNotSigned: number;
	unreadMessages: number;
	stateText?: string;
}

const elements = {
	actions: styled.div`
		display: flex;
		flex-shrink: 0;
		padding-left: calc(48px + ${tokens.spacing.x2_5});
		margin: 0 calc(var(--cellula--space--x0_5) / 2 * -1); // 1/2 Padding to not lose the tooltip with mouse left. Padding is used to give each item equal space.
		${tokens.breakpointQuery.medium} {
			justify-content: space-between;
		}
	`,
	action: styled.div`
		flex-shrink: 0;
		display: flex;
		align-items: center;
		gap: ${tokens.spacing.x0_25};
		padding: 0 calc(${tokens.spacing.x1_5} / 2); // 2/2 Padding to not lose the tooltip with mouse left. Padding is used to give each item equal space.
		color: ${tokens.color.content.muted};
	`,
	arrow: styled.div`
		width: 20px;
		position: absolute;
		right: ${tokens.spacing.x1_5};
		bottom: ${tokens.spacing.x1_5};
		display: block;
		color: ${tokens.color.content.default};

		${tokens.breakpointQuery.medium} {
			display: none;
		}
	`,
	labels: styled.div`
		display: flex;
		flex-direction: column-reverse;
		align-items: flex-end;
		position: absolute;
		top: ${tokens.spacing.x1_5};
		right: ${tokens.spacing.x1_5};
		gap: ${tokens.spacing.x0_5};

		${tokens.breakpointQuery.medium} {
			flex-direction: unset;
		}
	`,
	actionIconWrapper: styled.div`
		display: flex;
		margin: 0 calc((${tokens.spacing.x1} + 0.15rem) * -1); // The 0.15rem is just to visually align with the text content. Is a bit of a hack tbh
	`,
};

/**
 * Visual information sections about the case.
 */
export function ComprehensiveInfo({
	reservedUntil,
	pendingPayment,
	formsNotSigned,
	unreadMessages,
	stateText,
	label,
	labelIcon,
	isNew,
}: Props): JSX.Element {
	return (
		<Fragment>
			<Labels isNew={isNew} label={label} labelIcon={labelIcon} />

			<ActionContainer
				reservedUntil={reservedUntil}
				formsNotSigned={formsNotSigned}
				pendingPayment={pendingPayment}
				unreadMessages={unreadMessages}
				stateText={stateText}
			/>

			<ArrowForMobile />
		</Fragment>
	);
}

interface LabelProps {
	isNew?: boolean;
	label?: string;
	labelIcon?: IconProps['type'];
}

function Labels({ isNew, label, labelIcon }: LabelProps): JSX.Element | null {
	if (label == null && isNew == null) return null;

	return (
		<elements.labels>
			{isNew ? <BadgeText>{withTranslation('PW.Component.CaseCard.Label.NewIncoming')}</BadgeText> : null}
			{label != null ? (
				<BadgeText icon={labelIcon} variant='brandLight'>
					{label}
				</BadgeText>
			) : null}
		</elements.labels>
	);
}

interface ActionContainerProps {
	reservedUntil?: CounterState;
	pendingPayment: number;
	formsNotSigned: number;
	unreadMessages: number;
	stateText?: string;
}

function ActionContainer({
	reservedUntil,
	formsNotSigned,
	pendingPayment,
	unreadMessages,
	stateText,
}: ActionContainerProps): JSX.Element | null {
	if (formsNotSigned <= 0 && pendingPayment <= 0 && unreadMessages <= 0 && stateText == null) return null;

	return (
		<elements.actions>
			{stateText ? (
				<Text
					variant='labelSmallRegular'
					style={{
						color: tokens.color.content.default,
					}}
				>
					{stateText}
				</Text>
			) : null}

			<elements.actionIconWrapper>
				<Action
					icon='notesBoardPencil'
					amount={formsNotSigned}
					tooltipText={withTranslation('PW.Component.CaseCard.Tooltip.AwaitingFormResponse')}
				/>
				<ActionPayment reservedUntil={reservedUntil} amount={pendingPayment} />
				<Action
					icon='speechBubbleLines'
					amount={unreadMessages}
					tooltipText={withTranslation('PW.Component.CaseCard.Tooltip.UnreadMessages')}
				/>
			</elements.actionIconWrapper>
		</elements.actions>
	);
}

interface ActionProps {
	tooltipText: string;
	icon: IconProps['type'];
	amount: number;
}

function Action({ amount, icon, tooltipText }: ActionProps): JSX.Element | null {
	if (amount <= 0) return null;

	return (
		<Tooltip content={tooltipText}>
			<elements.action>
				<Icon type={icon} />
				<BadgeNumber decorative value={amount} size='small' clampMax={99} />
			</elements.action>
		</Tooltip>
	);
}

interface ArrowForMobileProps {}

function ArrowForMobile(_: ArrowForMobileProps): JSX.Element {
	return (
		<elements.arrow>
			<Icon type='arrowRight' />
		</elements.arrow>
	);
}

interface ActionPayment {
	reservedUntil?: CounterState;
	amount: number;
}

function ActionPayment({ amount, reservedUntil }: ActionPayment): JSX.Element | null {
	if (amount <= 0) return null;

	return (
		<Stack gap={tokens.spacing.x1} direction='horizontal'>
			<Action icon='wallet' amount={amount} tooltipText={withTranslation('PW.Component.CaseCard.Tooltip.AwaitingPayment')} />

			{reservedUntil ? <CountDownerBadge reservedUntil={reservedUntil} /> : null}
		</Stack>
	);
}

interface CountDownerProps {
	reservedUntil: CounterState;
}

function CountDownerBadge({ reservedUntil }: CountDownerProps): JSX.Element {
	const { time } = useCounterState(reservedUntil);

	return (
		<BadgeText variant='warning'>
			{withTranslation('PW.ReservedUntil.Badge', {
				min: time.toString(),
			})}
		</BadgeText>
	);
}
